<script setup lang="ts">
import { onMounted, ref } from 'vue';

import OutcomeBanner from '../../components/OutcomeBanner.vue';
import { adminOutcome, api, type Outcome } from '../../api/client.js';
import { adminCommand, uuid } from '../../api/documents.js';
import { saoJoaoSummary, seedSaoJoao } from './sao-joao-demo.js';

interface Tenant {
  id: string;
  name: string;
  code: string;
  status: string;
}

const tenants = ref<Tenant[]>([]);
const name = ref('');
const outcome = ref<Outcome | null>(null);
const busy = ref(false);
const demoProgress = ref<string | null>(null);
const emit = defineEmits<{ select: [tenant: Tenant] }>();
const demoSummary = saoJoaoSummary();

async function load(): Promise<void> {
  const result = await api.list<{ items: Tenant[] }>('/admin/tenants?limit=100');
  tenants.value = result.body.items ?? [];
}

async function create(): Promise<void> {
  busy.value = true;
  try {
    const tenantId = uuid();
    // O código do cliente NÃO é informado: ele é resultado da criação.
    const result = await api.command<Record<string, unknown>>(
      adminCommand(tenantId, 'tenant_create', { name: name.value }),
    );
    outcome.value = adminOutcome(result);
    if (outcome.value.kind === 'applied') {
      name.value = '';
      await load();
    }
  } finally {
    busy.value = false;
  }
}

async function createSaoJoaoDemo(): Promise<void> {
  busy.value = true;
  outcome.value = null;
  try {
    const result = await seedSaoJoao((message) => {
      demoProgress.value = message;
    });
    await load();
    outcome.value = {
      kind: 'applied',
      message: `São João pronta com ${result.pastureCount} pastos e ${result.poleCount} postes.`,
      errorCode: null,
    };
    emit('select', result.tenant);
  } catch (error) {
    outcome.value = {
      kind: 'rejected',
      message: error instanceof Error ? error.message : 'Não foi possível preparar a demonstração.',
      errorCode: 'DEMO_SEED_FAILED',
    };
  } finally {
    demoProgress.value = null;
    busy.value = false;
  }
}

onMounted(load);
</script>

<template>
  <section>
    <h2>Clientes</h2>
    <form
      class="row"
      @submit.prevent="create"
    >
      <label>
        Nome
        <input
          v-model="name"
          name="tenant-name"
          required
        >
      </label>
      <button
        type="submit"
        :disabled="busy || name.trim().length === 0"
      >
        Criar cliente
      </button>
    </form>
    <OutcomeBanner :outcome="outcome" />

    <aside class="demo-card">
      <div>
        <strong>Demonstração São João</strong>
        <p>
          Cria ou retoma automaticamente a fazenda com {{ demoSummary.pastureCount }}
          pastos e {{ demoSummary.poleCount }} postes nas coordenadas do mock original.
        </p>
        <p
          v-if="demoProgress !== null"
          role="status"
        >
          {{ demoProgress }}
        </p>
      </div>
      <button
        type="button"
        :disabled="busy"
        @click="createSaoJoaoDemo"
      >
        {{ busy && demoProgress !== null ? 'Preparando…' : 'Preparar São João' }}
      </button>
    </aside>

    <table>
      <thead>
        <tr><th>Nome</th><th>Código</th><th>Situação</th><th /></tr>
      </thead>
      <tbody>
        <tr
          v-for="tenant in tenants"
          :key="tenant.id"
          :data-tenant="tenant.id"
        >
          <td>{{ tenant.name }}</td>
          <td><code>{{ tenant.code }}</code></td>
          <td>{{ tenant.status }}</td>
          <td>
            <button
              type="button"
              @click="emit('select', tenant)"
            >
              Operar
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="tenants.length === 0">
      Nenhum cliente cadastrado.
    </p>
  </section>
</template>

<style scoped>
.row { display: flex; gap: .75rem; align-items: end; margin-bottom: .75rem; }
.demo-card { align-items: center; background: #f2f7f4; border: 1px solid #a9c7b4; border-radius: .5rem; display: flex; gap: 1rem; justify-content: space-between; margin: 1rem 0; padding: 1rem; }
.demo-card p { margin: .35rem 0 0; }
table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
th, td { border-bottom: 1px solid #ddd; text-align: left; padding: .4rem; }
</style>
